import { t } from '@lingui/macro';

export const DEFAULT_COUNTRY_CODE = 'CA';

const canadianRegions = [
    ['AB', () => t`Alberta`],
    ['BC', () => t`British Columbia`],
    ['MB', () => t`Manitoba`],
    ['NB', () => t`New Brunswick`],
    ['NL', () => t`Newfoundland and Labrador`],
    ['NS', () => t`Nova Scotia`],
    ['NU', () => t`Nunavut`],
    ['NT', () => t`Northwest Territories`],
    ['ON', () => t`Ontario`],
    ['PE', () => t`Prince Edward Island`],
    ['QC', () => t`Quebec`],
    ['SK', () => t`Saskatchewan`],
    ['YT', () => t`Yukon`],
];

const unitedStatesRegions = [
    ['AL', () => t`Alabama`],
    ['AK', () => t`Alaska`],
    ['AZ', () => t`Arizona`],
    ['AR', () => t`Arkansas`],
    ['CA', () => t`California`],
    ['CO', () => t`Colorado`],
    ['CT', () => t`Connecticut`],
    ['DE', () => t`Delaware`],
    ['DC', () => t`District Of Columbia`],
    ['FL', () => t`Florida`],
    ['GA', () => t`Georgia`],
    ['HI', () => t`Hawaii`],
    ['ID', () => t`Idaho`],
    ['IL', () => t`Illinois`],
    ['IN', () => t`Indiana`],
    ['IA', () => t`Iowa`],
    ['KS', () => t`Kansas`],
    ['KY', () => t`Kentucky`],
    ['LA', () => t`Louisiana`],
    ['ME', () => t`Maine`],
    ['MD', () => t`Maryland`],
    ['MA', () => t`Massachusetts`],
    ['MI', () => t`Michigan`],
    ['MN', () => t`Minnesota`],
    ['MS', () => t`Mississippi`],
    ['MO', () => t`Missouri`],
    ['MT', () => t`Montana`],
    ['NE', () => t`Nebraska`],
    ['NV', () => t`Nevada`],
    ['NH', () => t`New Hampshire`],
    ['NJ', () => t`New Jersey`],
    ['NM', () => t`New Mexico`],
    ['NY', () => t`New York`],
    ['NC', () => t`North Carolina`],
    ['ND', () => t`North Dakota`],
    ['OH', () => t`Ohio`],
    ['OK', () => t`Oklahoma`],
    ['OR', () => t`Oregon`],
    ['PA', () => t`Pennsylvania`],
    ['RI', () => t`Rhode Island`],
    ['SC', () => t`South Carolina`],
    ['SD', () => t`South Dakota`],
    ['TN', () => t`Tennessee`],
    ['TX', () => t`Texas`],
    ['UT', () => t`Utah`],
    ['VT', () => t`Vermont`],
    ['VA', () => t`Virginia`],
    ['WA', () => t`Washington`],
    ['WV', () => t`West Virginia`],
    ['WI', () => t`Wisconsin`],
    ['WY', () => t`Wyoming`],
    ['PR', () => t`Puerto Rico`],
    ['VI', () => t`Virgin Islands`],
    ['MP', () => t`Northern Mariana Islands`],
    ['GU', () => t`Guam`],
    ['AS', () => t`American Samoa`],
    ['PW', () => t`Palau`],
];

const alphanumericPostalCode = (value, maxLength) =>
    value
        .replace(/[^0-9a-zA-Z]/g, '')
        .slice(0, maxLength)
        .toUpperCase();

const numericPostalCode = (value, maxLength) => value.replace(/\D/g, '').slice(0, maxLength);


export const countryProfiles = [
    {
        code: 'CA',
        aliases: ['CANADA'],
        label: () => t`Canada`,
        phone: { minLength: 10, maxLength: 11 },
        postalCode: {
            label: () => t`Postal Code`,
            errorMessage: () => t`Please enter a valid postal code`,
            normalize: (value) => alphanumericPostalCode(value, 6),
            isValid: (value) => /^[A-Z]\d[A-Z]\d[A-Z]\d$/i.test(value),
        },
        region: {
            mode: 'select',
            label: () => t`Province`,
            errorMessage: () => t`Please select a Province`,
            required: true,
            options: canadianRegions,
        },
    },
    {
        code: 'US',
        aliases: ['UNITED STATES'],
        label: () => t`United States`,
        phone: { minLength: 10, maxLength: 11 },
        postalCode: {
            label: () => t`ZIP`,
            errorMessage: () => t`Please enter a valid ZIP code`,
            normalize: (value) => numericPostalCode(value, 9),
            isValid: (value) => /^\d{5}(\d{4})?$/.test(value),
        },
        region: {
            mode: 'select',
            label: () => t`State`,
            errorMessage: () => t`Please select a State`,
            required: true,
            options: unitedStatesRegions,
        },
    },
    {
        code: 'GB',
        aliases: ['UNITED KINGDOM'],
        label: () => t`United Kingdom`,
        phone: { minLength: 10, maxLength: 12 },
        postalCode: {
            label: () => t`Postcode`,
            errorMessage: () => t`Please enter a valid postcode`,
            normalize: (value) => alphanumericPostalCode(value, 8),
            isValid: (value) => value.length >= 6 && value.length <= 8,
        },
        region: { mode: 'hidden' },
    },
    {
        code: 'AU',
        aliases: ['AUSTRALIA'],
        label: () => t`Australia`,
        phone: { minLength: 10, maxLength: 11 },
        postalCode: {
            label: () => t`Postcode`,
            errorMessage: () => t`Please enter a valid postcode`,
            normalize: (value) => alphanumericPostalCode(value, 4),
            isValid: (value) => value.length === 4,
        },
        region: { mode: 'hidden' },
    },
    {
        code: 'NL',
        aliases: ['NETHERLANDS'],
        label: () => t`Netherlands`,
        phone: { minLength: 10, maxLength: 11 },
        postalCode: {
            label: () => t`Postcode`,
            errorMessage: () => t`Please enter a valid postcode`,
            normalize: (value) => alphanumericPostalCode(value, 6),
            isValid: (value) => value.length === 6,
        },
        region: { mode: 'hidden' },
    },
    {
        code: 'ES',
        aliases: ['SPAIN'],
        label: () => t`Spain`,
        phone: { minLength: 9, maxLength: 11 },
        postalCode: {
            label: () => t`Postal Code`,
            errorMessage: () => t`Please enter a valid postal code`,
            normalize: (value) => numericPostalCode(value, 5),
            isValid: (value) => /^\d{5}$/.test(value),
        },
        region: { mode: 'hidden' },
    },
    {
        code: 'BE',
        aliases: ['BELGIUM'],
        label: () => t`Belgium`,
        phone: { minLength: 9, maxLength: 11 },
        postalCode: {
            label: () => t`Postal Code`,
            errorMessage: () => t`Please enter a valid postal code`,
            normalize: (value) => numericPostalCode(value, 4),
            isValid: (value) => /^\d{4}$/.test(value),
        },
        region: { mode: 'hidden' },
    },
    {
        code: 'CH',
        aliases: ['SWITZERLAND'],
        label: () => t`Switzerland`,
        phone: { minLength: 9, maxLength: 11 },
        postalCode: {
            label: () => t`Postal Code`,
            errorMessage: () => t`Please enter a valid postal code`,
            normalize: (value) => numericPostalCode(value, 4),
            isValid: (value) => /^\d{4}$/.test(value),
        },
        region: { mode: 'hidden' },
    },
    {
        code: 'DE',
        aliases: ['GERMANY'],
        label: () => t`Germany`,
        phone: { minLength: 6, maxLength: 13 },
        postalCode: {
            label: () => t`Postal Code`,
            errorMessage: () => t`Please enter a valid postal code`,
            normalize: (value) => numericPostalCode(value, 5),
            isValid: (value) => /^\d{5}$/.test(value),
        },
        region: { mode: 'hidden' },
    },
    {
        code: 'FR',
        aliases: ['FRANCE'],
        label: () => t`France`,
        phone: { minLength: 10, maxLength: 11 },
        postalCode: {
            label: () => t`Postal Code`,
            errorMessage: () => t`Please enter a valid postal code`,
            normalize: (value) => numericPostalCode(value, 5),
            isValid: (value) => /^\d{5}$/.test(value),
        },
        region: { mode: 'hidden' },
    },
];

const profilesByCode = countryProfiles.reduce((profiles, profile) => {
    profiles[profile.code] = profile;
    return profiles;
}, {});

export function getCountryProfile(countryCode) {
    return profilesByCode[countryCode] || profilesByCode[DEFAULT_COUNTRY_CODE];
}

export function normalizeCountryCode(country) {
    if (typeof country !== 'string') return DEFAULT_COUNTRY_CODE;

    const normalized = country.trim().toUpperCase();
    const profile = countryProfiles.find(
        (candidate) => candidate.code === normalized || candidate.aliases.includes(normalized),
    );

    return profile ? profile.code : DEFAULT_COUNTRY_CODE;
}

export function normalizePostalCode(postalCode, countryCode) {
    return getCountryProfile(countryCode).postalCode.normalize(postalCode);
}

export function isValidPostalCode(postalCode, countryCode) {
    return getCountryProfile(countryCode).postalCode.isValid(postalCode);
}

export function normalizePhone(phone, countryCode) {
    return phone.replace(/\D/g, '').slice(0, getCountryProfile(countryCode).phone.maxLength);
}

export function isValidPhone(phone, countryCode) {
    const { minLength, maxLength } = getCountryProfile(countryCode).phone;
    return phone.length >= minLength && phone.length <= maxLength;
}
